<!--
    צבעי לוח שנה - the settings-page block (הגדרות ארגון → ראשי) where the calendar's colors are picked:
    events by their status (מצב פניה, and סטטוס תהליך פנימי on top), meetings by their meeting type, tasks.

    The colors are edited right in $store.state.businessSettings (calendarEventColor / calendarMeetingColor /
    calendarTaskColor + the calendarColors json), so every open calendar repaints while picking
    (App.vue → calendar-colors.js); they are saved with the tab's שמור (Settings.saveGeneralTab).
    Each row shows a chip drawn the way the calendar draws the item.
-->
<template>
    <div class="calendarColorsSettings">

        <div class="calendarColorsIntro">
            הצבעים של האירועים, הפגישות, המשימות והפריטים האישיים ביומן (תצוגת חודש, שבוע ויום). אירוע נצבע לפי מצב הפניה שלו,
            ואם לסטטוס התהליך הפנימי שלו בחרתם צבע - לפיו. פגישה נצבעת לפי סוג הפגישה. השינויים נשמרים בלחיצה על "שמור".
        </div>

        <div class="row">

            <div class="col-md-5 calendarColorsColumn">
                <label class="form-label">אירועים לפי מצב פניה</label>

                <div v-for="row in systemStatusRows"
                     :key="row.key"
                     class="calendarColorRow"
                     :data-color-key="row.key">
                    <label class="calendarColorSwatch"
                           :style="{ background: row.pickerValue }"
                           title="בחירת צבע">
                        <input type="color"
                               :value="row.pickerValue"
                               @input="setColor(row, $event.target.value)">
                    </label>
                    <span class="calendarColorChip"
                          :style="row.chipStyle">{{ row.name }}</span>
                    <i v-if="row.color"
                       class="fa-solid fa-rotate-left calendarColorReset"
                       title="חזרה לצבע ברירת המחדל"
                       @click="setColor(row, null)"></i>
                </div>

                <template v-if="eventStatusRows.length">
                    <label class="form-label calendarColorsSubtitle">סטטוס תהליך פנימי</label>
                    <div class="calendarColorsNote">
                        צבע של סטטוס גובר על צבע מצב הפניה. סטטוס בלי צבע - האירוע נצבע לפי מצב הפניה.
                    </div>

                    <div v-for="row in eventStatusRows"
                         :key="row.key"
                         class="calendarColorRow"
                         :data-color-key="row.key">
                        <label class="calendarColorSwatch"
                               :class="{ calendarColorSwatchEmpty: !row.color }"
                               :style="row.color ? { background: row.color } : {}"
                               title="בחירת צבע">
                            <input type="color"
                                   :value="row.pickerValue"
                                   @input="setColor(row, $event.target.value)">
                        </label>
                        <span class="calendarColorChip"
                              :style="row.chipStyle">{{ row.name }}</span>
                        <small v-if="!row.color"
                               class="calendarColorFallback">לפי מצב הפניה</small>
                        <i v-if="row.color"
                           class="fa-solid fa-rotate-left calendarColorReset"
                           title="בלי צבע (לפי מצב הפניה)"
                           @click="setColor(row, null)"></i>
                    </div>
                </template>
            </div>

            <div class="col-md-4 calendarColorsColumn">
                <label class="form-label">פגישות לפי סוג פגישה</label>

                <div v-for="row in meetingRows"
                     :key="row.key"
                     class="calendarColorRow"
                     :data-color-key="row.key">
                    <label class="calendarColorSwatch"
                           :style="{ background: row.pickerValue }"
                           title="בחירת צבע">
                        <input type="color"
                               :value="row.pickerValue"
                               @input="setColor(row, $event.target.value)">
                    </label>
                    <span class="calendarColorChip"
                          :style="row.chipStyle">{{ row.name }}</span>
                    <small v-if="row.note"
                           class="calendarColorFallback">{{ row.note }}</small>
                    <small v-else-if="row.fallbackText && !row.color"
                           class="calendarColorFallback">{{ row.fallbackText }}</small>
                    <i v-if="row.color"
                       class="fa-solid fa-rotate-left calendarColorReset"
                       :title="row.field ? 'חזרה לצבע ברירת המחדל' : 'בלי צבע (כמו פגישה)'"
                       @click="setColor(row, null)"></i>
                </div>

                <div v-if="meetingRows.length == 1"
                     class="calendarColorsNote">
                    עדיין לא הוגדרו סוגי פגישות. אפשר להוסיף אותם בטופס הפגישה, בשדה סוג הפגישה.
                </div>
            </div>

            <div class="col-md-3 calendarColorsColumn">
                <label class="form-label">משימות</label>

                <div class="calendarColorRow"
                     :data-color-key="taskRow.key">
                    <label class="calendarColorSwatch"
                           :style="{ background: taskRow.pickerValue }"
                           title="בחירת צבע">
                        <input type="color"
                               :value="taskRow.pickerValue"
                               @input="setColor(taskRow, $event.target.value)">
                    </label>
                    <span class="calendarColorChip"
                          :style="taskRow.chipStyle">{{ taskRow.name }}</span>
                    <i v-if="taskRow.color"
                       class="fa-solid fa-rotate-left calendarColorReset"
                       title="חזרה לצבע ברירת המחדל"
                       @click="setColor(taskRow, null)"></i>
                </div>

                <!-- אישי: the calendar's personal items (מילואים / רופא...) -->
                <label class="form-label calendarColorsSubtitle">אישי</label>

                <div class="calendarColorRow"
                     :data-color-key="personalRow.key">
                    <label class="calendarColorSwatch"
                           :style="{ background: personalRow.pickerValue }"
                           title="בחירת צבע">
                        <input type="color"
                               :value="personalRow.pickerValue"
                               @input="setColor(personalRow, $event.target.value)">
                    </label>
                    <span class="calendarColorChip"
                          :style="personalRow.chipStyle">{{ personalRow.name }}</span>
                    <i v-if="personalRow.color"
                       class="fa-solid fa-rotate-left calendarColorReset"
                       title="חזרה לצבע ברירת המחדל"
                       @click="setColor(personalRow, null)"></i>
                </div>

                <!-- by user: whose items wear which color (calendarColors.personalUser) -->
                <template v-if="personalUserRows.length > 1">
                    <div class="calendarColorsNote">
                        צבע למשתמש גובר על הצבע של אישי. משתמש בלי צבע - הפריטים שלו בצבע של אישי.
                    </div>

                    <div v-for="row in personalUserRows"
                         :key="row.key"
                         class="calendarColorRow"
                         :data-color-key="row.key">
                        <label class="calendarColorSwatch"
                               :class="{ calendarColorSwatchEmpty: !row.color }"
                               :style="row.color ? { background: row.color } : {}"
                               title="בחירת צבע">
                            <input type="color"
                                   :value="row.pickerValue"
                                   @input="setColor(row, $event.target.value)">
                        </label>
                        <span class="calendarColorChip"
                              :style="row.chipStyle">{{ row.name }}</span>
                        <small v-if="!row.color"
                               class="calendarColorFallback">{{ row.fallbackText }}</small>
                        <i v-if="row.color"
                           class="fa-solid fa-rotate-left calendarColorReset"
                           title="בלי צבע (כמו אישי)"
                           @click="setColor(row, null)"></i>
                    </div>
                </template>
            </div>

        </div>

    </div>
</template>
<script>
import {
    CALENDAR_SYSTEM_STATUSES, DEFAULT_LOOKS, DEFAULT_MEETING_COLOR, DEFAULT_TASK_COLOR, DEFAULT_PERSONAL_COLOR,
    systemStatusKey, systemStatusColor, calendarColorOf, validColor, itemLookStyle
} from '../../services/calendar/calendar-colors';

// a status with no color of its own: the chip says so instead of showing a color
const NO_COLOR_CHIP = { background: '#f7f7f7', color: '#777777', borderColor: '#cccccc', borderStyle: 'dashed' };

export default {
    name: 'CalendarColorsSettings',

    computed: {

        settings() {
            return this.$store.state.businessSettings;
        },

        // פניה חדשה … אירוע סגור; אירוע סגור is calendarEventColor, the rest live in calendarColors
        systemStatusRows() {
            const labels = this.$store.state.planupSystemStatuses || [];
            return CALENDAR_SYSTEM_STATUSES.map(key => {
                const status = labels.find(item => systemStatusKey(item.value) == key);
                const look = DEFAULT_LOOKS[key];
                const color = systemStatusColor(this.settings, key);
                return {
                    key: 'systemStatus:' + key,
                    field: key == 'hazmanaSigned' ? 'calendarEventColor' : null,
                    group: 'systemStatus',
                    id: key,
                    name: status ? status.label : key,
                    color: color,
                    pickerValue: color || look.fill || look.outline || look.stripes,
                    chipStyle: itemLookStyle(color, look)
                };
            });
        },

        // the account's own statuses; one that cancels the פניה is left out - its events leave the calendar
        eventStatusRows() {
            return (this.$store.state.planupEventStatuses || [])
                .filter(status => !status.hidden && !status.cancelsPnia)
                .map(status => {
                    const color = calendarColorOf(this.settings, 'eventStatus', status.id);
                    return {
                        key: 'eventStatus:' + status.id,
                        group: 'eventStatus',
                        id: status.id,
                        name: status.name,
                        color: color,
                        pickerValue: color || '#ab8ce4',
                        chipStyle: color ? itemLookStyle(color) : NO_COLOR_CHIP
                    };
                });
        },

        // the meeting color (a meeting with no type, or of a type with no color), then each type
        meetingRows() {
            const meetingColor = validColor(this.settings.calendarMeetingColor);
            const effectiveMeetingColor = meetingColor || DEFAULT_MEETING_COLOR;

            const rows = [{
                key: 'meeting',
                field: 'calendarMeetingColor',
                name: 'פגישה',
                color: meetingColor,
                pickerValue: effectiveMeetingColor,
                chipStyle: itemLookStyle(effectiveMeetingColor),
                note: 'ללא סוג, וכל סוג בלי צבע'
            }];

            (this.$store.state.planupMeetingTypes || [])
                .filter(type => !type.hidden)
                .forEach(type => {
                    const color = calendarColorOf(this.settings, 'meetingType', type.id);
                    rows.push({
                        key: 'meetingType:' + type.id,
                        group: 'meetingType',
                        id: type.id,
                        name: type.name,
                        color: color,
                        pickerValue: color || effectiveMeetingColor,
                        chipStyle: itemLookStyle(color || effectiveMeetingColor),
                        fallbackText: 'כמו פגישה'
                    });
                });

            return rows;
        },

        taskRow() {
            const color = validColor(this.settings.calendarTaskColor);
            return {
                key: 'task',
                field: 'calendarTaskColor',
                name: 'משימה',
                color: color,
                pickerValue: color || DEFAULT_TASK_COLOR,
                chipStyle: itemLookStyle(color || DEFAULT_TASK_COLOR)
            };
        },

        // אישי - calendarColors.other.personal
        personalRow() {
            const color = calendarColorOf(this.settings, 'other', 'personal');
            return {
                key: 'personal',
                group: 'other',
                id: 'personal',
                name: 'אישי',
                color: color,
                pickerValue: color || DEFAULT_PERSONAL_COLOR,
                chipStyle: itemLookStyle(color || DEFAULT_PERSONAL_COLOR)
            };
        },

        // אישי by user - calendarColors.personalUser[users.id]; the account's users (tasks/assignees)
        personalUserRows() {
            const base = this.personalRow.color || DEFAULT_PERSONAL_COLOR;
            return (this.users || []).map(user => {
                const color = calendarColorOf(this.settings, 'personalUser', user.id);
                return {
                    key: 'personalUser:' + user.id,
                    group: 'personalUser',
                    id: user.id,
                    name: user.name,
                    color: color,
                    pickerValue: color || base,
                    chipStyle: itemLookStyle(color || base),
                    fallbackText: 'כמו אישי'
                };
            });
        },

    },

    data() {
        return {
            // the account's staff users, for the per-user rows of אישי
            users: [],
        };
    },

    mounted() {
        const apply = users => { this.users = Array.isArray(users) ? users : []; };
        apply(this.$store.state.taskAssignees);
        this.$handlers.tasksHandler.loadAssignees().then(apply).catch(() => { });
    },

    methods: {

        // null = back to the default (a base color) / no color of its own (a status, a meeting type).
        // $set throughout: calendarColors and its groups may be missing on the store object.
        setColor(row, color) {

            const settings = this.settings;

            if (row.field) {
                this.$set(settings, row.field, color);
                return;
            }

            if (!settings.calendarColors || typeof settings.calendarColors != 'object')
                this.$set(settings, 'calendarColors', {});

            const colors = settings.calendarColors;

            if (!colors[row.group] || typeof colors[row.group] != 'object')
                this.$set(colors, row.group, {});

            if (color)
                this.$set(colors[row.group], row.id, color);
            else
                this.$delete(colors[row.group], row.id);
        },

    },
}
</script>
<style>
.calendarColorsSettings .calendarColorsIntro {
    color: #777;
    margin-bottom: 14px;
}

.calendarColorsSettings .calendarColorsColumn {
    margin-bottom: 16px;
}

.calendarColorsSettings .calendarColorsSubtitle {
    margin-top: 14px;
}

.calendarColorsSettings .calendarColorsNote {
    font-size: 12px;
    color: #777;
    margin-bottom: 8px;
}

.calendarColorsSettings .calendarColorRow {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 7px;
    min-height: 28px;
}

/* the swatch is the picker: an invisible color input stretched over it */
.calendarColorsSettings .calendarColorSwatch {
    position: relative;
    flex: 0 0 auto;
    width: 36px;
    height: 26px;
    margin: 0;
    border: 1px solid #d5d5d5;
    border-radius: 6px;
    cursor: pointer;
    overflow: hidden;
}

.calendarColorsSettings .calendarColorSwatch input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    opacity: 0;
    cursor: pointer;
}

.calendarColorsSettings .calendarColorSwatchEmpty {
    border-style: dashed;
    border-color: #bbb;
    background: repeating-linear-gradient(45deg, #ffffff, #ffffff 4px, #eeeeee 4px, #eeeeee 8px);
}

/* drawn like the calendar item */
.calendarColorsSettings .calendarColorChip {
    display: inline-block;
    max-width: 230px;
    padding: 2px 10px;
    border: 1px solid transparent;
    border-radius: 4px;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.calendarColorsSettings .calendarColorFallback {
    color: #999;
    white-space: nowrap;
}

.calendarColorsSettings .calendarColorReset {
    color: #999;
    cursor: pointer;
    font-size: 13px;
}

.calendarColorsSettings .calendarColorReset:hover {
    color: #555;
}
</style>
